import React from "react";
import { Alert, Card, Skeleton, Table } from "@mantine/core";
import { CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnApi } from "../../apis";
import type { IReplacementFeasibilityResponse } from "../../apis/return";
import RenderText from "../ui/render-text";

interface IProps {
  returnRequestId: string;
  enabled: boolean;
}

type Suggestion = IReplacementFeasibilityResponse["suggestion"];

const SUGGESTION_COLOR: Record<Suggestion, string> = {
  OK_TO_REPLACE_FULL: "green",
  CONSIDER_MIXED_RESOLUTION: "yellow",
  FALLBACK_TO_REFUND_MONEY: "red",
};

export function ReplacementFeasibilityWidget({ returnRequestId, enabled }: IProps): React.JSX.Element | null {
  const { t } = useTranslation("returns");
  const { data, error, isLoading } = useSWR(
    enabled ? ["replacement-feasibility", returnRequestId] : null,
    () => ReturnApi.replacementFeasibility(returnRequestId),
    { revalidateOnFocus: false }
  );

  if (!enabled) return null;

  if (isLoading) return <Skeleton h={120} />;

  if (error) {
    return (
      <Alert color="red" title={t("replacementFeasibility.loadError")}>
        {(error as Error).message}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <Card withBorder p="sm" radius="md">
      <Alert color={SUGGESTION_COLOR[data.suggestion]} mb="sm" p="xs">
        {t(`replacementFeasibility.suggestion.${data.suggestion}`)}
      </Alert>
      <Table withColumnBorders withRowBorders fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("replacementFeasibility.col.product")}</Table.Th>
            <Table.Th>SKU</Table.Th>
            <Table.Th>{t("replacementFeasibility.col.requested")}</Table.Th>
            <Table.Th>{t("replacementFeasibility.col.available")}</Table.Th>
            <Table.Th>{t("replacementFeasibility.col.shortage")}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.items.map((item) => (
            <Table.Tr
              key={item.returnItemId}
              bg={item.sufficient ? undefined : "red.0"}
            >
              <Table.Td>
                <RenderText text={item.productName} />
              </Table.Td>
              <Table.Td>
                <RenderText text={item.sku} color="dimmed" fallback="—" />
              </Table.Td>
              <Table.Td>
                <RenderText text={item.requestedQuantity} />
              </Table.Td>
              <Table.Td>
                <RenderText text={item.availableQuantity} />
              </Table.Td>
              <Table.Td>
                <RenderText
                  text={item.shortageQuantity > 0 ? item.shortageQuantity : null}
                  color={item.sufficient ? undefined : "red"}
                  fallback="—"
                />
              </Table.Td>
              <Table.Td>
                {item.sufficient ? (
                  <CheckCircleIcon size={18} color="var(--mantine-color-green-6)" />
                ) : (
                  <WarningCircleIcon size={18} color="var(--mantine-color-red-6)" />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
